import Career, { ICareer } from '@/models/Career'
import Tagging, { ITagging } from '@/models/Tagging'
import Topic, { ITopic } from '@/models/Topic'
import * as dayjs from 'dayjs'
import ModelBase, {IModelBase} from '@/models/ModelBase'
import { KeyPair } from '@/types/KeyPair'
import Event, {IEvent} from '@/models/Event'
import Applicant, { IApplicant} from '@/models/Applicant'
import {FavoriteRecruit, IFavoriteRecruit} from "@/models/FavoriteRecruit"

export interface IUser extends IModelBase {
  email?: string
  name?: string
  phone?: string
  gender?: string
  gender_text?: string
  birthday?: string
  zipcode?: string
  address?: string
  hope_job?: string
  present_job?: string
  hope_job_text?: string
  present_job_text?: string
  hope_job_comment?: string
  present_job_comment?: string
  experience_language_comment?: string
  ui_turn_result?: string
  ui_turn_reason_comment?: string
  recognition_route_comment?: string
  other_comment?: string
  progress_status?: string
  progress_status_text?: string
  progress_status_memo?: string
  experienced_companies?: number
  work_status?: string
  work_status_text?: string
  hope_income?: string
  referer?: string
  active: boolean
  ui_turn_timings?: Array<ITagging>
  ui_turn_reasons?: Array<ITagging>
  recognition_routes?: Array<ITagging>
  hope_jobs?: Array<ITagging>
  topics?: Array<ITopic>
  events?: Array<IEvent>
  careers?: Array<ICareer>
  applicants?: Array<IApplicant>
  favorite_recruits?: Array<IFavoriteRecruit>
  other_career_comment?: string
  other_hope_job_comment?: string
  topic_count?: number
  delete_at?: string
  receive_e_zine?: boolean
  agent_service?: boolean
  use_agent_service?: string
  use_agent_service_text?: string
  it_career_years?: number | null
  pg_career_years_for_business?: number | null
  pg_career_years_for_private?: number | null
  backlog_url?: string
  confirming_email?: boolean
  current_sign_in_at?: string
}

export default class User extends ModelBase {
  email?: string
  password?: string
  password_confirmation?: string
  name?: string
  phone?: string
  gender?: string
  gender_text?: string
  birthday?: string
  zipcode?: string
  address?: string
  hope_job_comment?: string
  present_job_comment?: string
  hope_job?: string
  present_job?: string
  hope_job_text?: string
  present_job_text?: string
  experience_language_comment?: string
  ui_turn_result?: string
  ui_turn_reason_comment?: string
  recognition_route_comment?: string
  other_comment?: string
  progress_status: string
  progress_status_text: string
  progress_status_memo: string
  experienced_companies?: number
  work_status?: string
  work_status_text?: string
  hope_income: string
  referer: string
  active = true
  ui_turn_timings: Array<Tagging> = []
  ui_turn_reasons: Array<Tagging> = []
  recognition_routes: Array<Tagging> = []
  hope_jobs: Array<Tagging> = []
  topics: Array<Topic> = []
  events: Array<Event> = []
  careers: Array<Career> = []
  applicants: Array<Applicant> = []
  favorite_recruits: Array<FavoriteRecruit> = []
  other_career_comment?: string
  other_hope_job_comment?: string
  topic_count = 0
  delete_at?: Date
  receive_e_zine = true
  agent_service = false
  use_agent_service?: string
  use_agent_service_text?: string
  it_career_years?: number | null
  pg_career_years_for_business?: number | null
  pg_career_years_for_private?: number | null
  backlog_url: string = null
  confirming_email = false
  unconfirmed_email?: string
  current_sign_in_at?: Date

  constructor(defaults?: IUser) {
    super(defaults)
    if (defaults) {
      this.email = defaults.email
      this.name = defaults.name || ''
      this.phone = defaults.phone || ''
      this.gender = defaults.gender || ''
      this.gender_text = defaults.gender_text || ''
      this.birthday = dayjs(defaults.birthday || new Date()).format('YYYY-MM-DD')
      this.zipcode = defaults.zipcode || ''
      this.address = defaults.address || ''
      this.hope_job = defaults.hope_job || ''
      this.present_job = defaults.present_job || ''
      this.hope_job_text = defaults.hope_job_text || ''
      this.present_job_text = defaults.present_job_text || ''
      this.hope_job_comment = defaults.hope_job_comment || ''
      this.present_job_comment = defaults.present_job_comment || ''
      this.experience_language_comment = defaults.experience_language_comment || ''
      this.ui_turn_result = defaults.ui_turn_result || ''
      this.ui_turn_reason_comment = defaults.ui_turn_reason_comment || ''
      this.recognition_route_comment = defaults.recognition_route_comment || ''
      this.other_comment = defaults.other_comment || ''
      this.progress_status = defaults.progress_status || ''
      this.progress_status_text = defaults.progress_status_text || ''
      this.progress_status_memo = defaults.progress_status_memo || ''
      this.experienced_companies = defaults.experienced_companies
      this.work_status = defaults.work_status
      this.work_status_text = defaults.work_status_text
      this.hope_income = defaults.hope_income
      this.referer = defaults.referer || ''
      this.active = defaults.active || false
      this.ui_turn_timings = defaults.ui_turn_timings?.map(tag => new Tagging(tag)) || []
      this.ui_turn_reasons = defaults.ui_turn_reasons?.map(tag => new Tagging(tag)) || []
      this.recognition_routes = defaults.recognition_routes?.map(tag => new Tagging(tag)) || []
      this.hope_jobs = defaults.hope_jobs?.map(tag => new Tagging(tag)) || []
      this.topics = defaults.topics?.map(t => new Topic(t)) || []
      this.events = defaults.events?.map(e => new Event(e)) || []
      this.careers = defaults.careers?.map(e => new Career(e)) || []
      this.applicants = defaults.applicants?.map(e => new Applicant(e)) || []
      this.applicants.forEach(a => a.user = this)
      this.favorite_recruits = defaults.favorite_recruits?.map(fr => new FavoriteRecruit(fr)) || []
      this.other_career_comment = defaults.other_career_comment
      this.other_hope_job_comment = defaults.other_hope_job_comment
      this.topic_count = defaults.topic_count
      this.delete_at = defaults.delete_at ? dayjs(defaults.delete_at).toDate() : undefined
      this.receive_e_zine = defaults.receive_e_zine || false
      this.agent_service = defaults.agent_service || false
      this.use_agent_service = defaults.use_agent_service
      this.use_agent_service_text = defaults.use_agent_service_text
      this.it_career_years = defaults.it_career_years
      this.pg_career_years_for_business = defaults.pg_career_years_for_business
      this.pg_career_years_for_private = defaults.pg_career_years_for_private
      this.backlog_url = defaults.backlog_url || null
      this.confirming_email = defaults.confirming_email
      this.current_sign_in_at = defaults.current_sign_in_at ? dayjs(defaults.current_sign_in_at).toDate() : undefined
    }
  }

  get deleted(): boolean {
    return !!this.delete_at
  }

  get uiTurnTimingId(): number {
    return this.ui_turn_timings[0]?.tag_id
  }

  set uiTurnTimingId(value: number) {
    this.ui_turn_timings = [new Tagging({ id: 0, tag_id: value })]
  }

  get uiTurnReasonIds(): Array<number> {
    return this.ui_turn_reasons.map(t => t.tag_id)
  }

  set uiTurnReasonIds(val: Array<number>) {
    this.ui_turn_reasons = val.map(v => new Tagging({ id: 0, tag_id: v }))
  }

  get recognitionRouteIds(): Array<number> {
    return this.recognition_routes.map(t => t.tag_id)
  }

  set recognitionRouteIds(val: Array<number>) {
    this.recognition_routes = val.map(v => new Tagging({ id: 0, tag_id: v }))
  }

  get activateString(): string {
    return this.active ? '本登録済' : '仮登録'
  }

  get uiTurnTimingString(): string {
    return this.ui_turn_timings[0]?.tag.name
  }

  get uiTurnReasonString(): string {
    return this.ui_turn_reasons.map(r => r.tag.name).join(',')
  }

  get recognitionRouteString(): string {
    return this.recognition_routes.map(r => r.tag.name).join(',')
  }

  get hopeJobsString(): string {
    return this.hope_jobs.map(r => r.tag.name).join(',')
  }

  get joinEventString(): string {
    return this.events.map(e => e.title).join(',')
  }

  get birthdayString(): string {
    return dayjs(this.birthday).format('YYYY/MM/DD')
  }

  get age(): number {
    return dayjs().diff(dayjs(this.birthday), 'year')
  }

  get invalidCareer(): boolean {
    return !this.careers.length && !this.other_career_comment
  }

  get invalidHopeJob(): boolean {
    return !this.hope_jobs.length && !this.other_hope_job_comment
  }

  get currentSignInAtString(): string {
    return this.current_sign_in_at ? dayjs(this.current_sign_in_at).format('YYYY/MM/DD HH:mm') : ''
  }

  toParamsBaseItem(): KeyPair {
    return {
      email: this.email,
      name: this.name,
      phone: this.phone,
      gender: this.gender,
      birthday: this.birthday,
      zipcode: this.zipcode,
      address: this.address,
      hope_job: this.hope_job,
      present_job: this.present_job,
      hope_job_comment: this.hope_job_comment,
      hope_income: this.hope_income,
      experienced_companies: this.experienced_companies,
      present_job_comment: this.present_job_comment,
      experience_language_comment: this.experience_language_comment,
      ui_turn_result: this.ui_turn_result,
      ui_turn_reason_comment: this.ui_turn_reason_comment,
      recognition_route_comment: this.recognition_route_comment,
      other_comment: this.other_comment,
      progress_status: this.progress_status,
      progress_status_memo: this.progress_status_memo,
      other_career_comment: this.other_career_comment,
      other_hope_job_comment: this.other_hope_job_comment,
      work_status: this.work_status,
      referer: this.referer,
      receive_e_zine: this.receive_e_zine,
      agent_service: this.agent_service,
      use_agent_service: this.use_agent_service,
      it_career_years: this.it_career_years,
      pg_career_years_for_business: this.pg_career_years_for_business,
      pg_career_years_for_private: this.pg_career_years_for_private,
      unconfirmed_email: this.unconfirmed_email
    }
  }

  toParams(): KeyPair {
    return Object.assign({}, this.toParamsBaseItem(), {
      ui_turn_timing_ids: this.ui_turn_timings.map(tag => tag.tag_id),
      ui_turn_reason_ids: this.ui_turn_reasons.map(tag => tag.tag_id),
      hope_job_ids: this.hope_jobs.map(tag => tag.tag_id),
      careers: this.careers.map(career => career.toParams())
    })
  }

  toParamsSignUp(): KeyPair {
    return Object.assign({}, this.toParams(), {
      password: this.password,
      password_confirmation: this.password_confirmation,
      recognition_route_ids: this.recognition_routes.map(tag => tag.tag_id)
    })
  }
}
